import { BackendCompatibleLocales, VenueStatus } from '../../enums';
import { z } from 'zod';
import { canvasImageSchema } from '../shared/canvasImage.schema';
import { TranslationValidatorSchema } from './translation.validator';
import { getTranslationValidationConfigurable } from '../../helpers/getTranslationValidation';

export const VenueValidatorSchema = z.object({
  timeZone: z.string().optional(),
  status: z.enum(VenueStatus),
  shortName: z.array(TranslationValidatorSchema),
  slug: z.string(),
  country: z.array(TranslationValidatorSchema),
  city: z.array(TranslationValidatorSchema),
  conditions: z.array(TranslationValidatorSchema),
  // Only the admin venue form sets this; the FGO edit screen leaves it as-is.
  barcodeType: z.string().optional(),
  longitude: z.number(),
  latitude: z.number(),
  currencyCode: z.string(),
  name: z.array(TranslationValidatorSchema),
  imageFileId: z.any().optional(),
  /** Optional company ID when creating from company settings. Null/undefined means global admin. */
  companyId: z.string().optional().nullable(),
  /** Audience that should receive the VENUE_CREATED activity; without it none is recorded. */
  fanGroupId: z.string().optional().nullable(),
});

export type VenueValidator = z.infer<typeof VenueValidatorSchema>;

/**
 * Body of the fan-group-owner venue create endpoint. `status` and `slug` are
 * assigned by the backend there; only the admin form sends them.
 */
export type FGOVenueCreateBody = Omit<VenueValidator, 'status' | 'slug'> &
  Partial<Pick<VenueValidator, 'status' | 'slug'>>;

const requiredTranslation = BackendCompatibleLocales.EN;

export const NotEmptyDefaultTranslationValidationSchema = z
  .partialRecord(z.enum(BackendCompatibleLocales), z.string().optional())
  .refine(
    (data) => {
      return (
        data &&
        data[requiredTranslation] &&
        data[requiredTranslation].trim() !== ''
      );
    },
    {
      message: 'Default translation (EN) for field must have non-empty text',
      path: [requiredTranslation],
    }
  );

const NotEmptyDefaultTranslationValidationSchemaWithLimit = getTranslationValidationConfigurable(
  BackendCompatibleLocales,
  {
    limit: 255,
    maxErrorMessage: 'validation_name_too_long',
    requireNonEmptyEn: true,
    requiredTranslationKey: 'EN',
  }
);

const stringOrCanvasSchema = z.union([
  z.string().min(1, 'venue_image_form_error_message'),
  canvasImageSchema,
]);

export const VenueFormValidatorSchema = z.object({
  // Seeded by getInitialValues (the venue's zone, or '' when creating). Required
  // because the backend only stores a zone its tz database knows, so an empty
  // value has to surface on the field instead of failing the save.
  timeZone: z
    .string({ error: 'general_label_required' })
    .min(1, 'general_label_required'),
  status: z.enum(VenueStatus),
  /**
   * Google Places search box backing the location picker; it drives
   * city/country/latitude/longitude. Seeded in both branches of
   * getInitialValues, and never validated — matching
   * {@link FGOVenueFormValidatorSchema}.
   */
  addressLine: z.string(),
  /**
   * Only seeded when creating; the submit handler takes the venue's own
   * barcode type when editing.
   */
  barcodeType: z.string().optional(),
  shortName: NotEmptyDefaultTranslationValidationSchemaWithLimit,
  slug: z.string().min(1),
  country: NotEmptyDefaultTranslationValidationSchema,
  city: NotEmptyDefaultTranslationValidationSchema,
  conditions: NotEmptyDefaultTranslationValidationSchema,
  // barcodeType: z.string(),
  longitude: z.union([z.string(), z.number()]),
  latitude: z.union([z.string(), z.number()]),
  currencyCode: z.string(),
  name: NotEmptyDefaultTranslationValidationSchemaWithLimit,
  imageUrl: stringOrCanvasSchema,
});

/** Form-state shape of the admin venue form (locale-keyed translations). */
export type VenueFormValidator = z.infer<typeof VenueFormValidatorSchema>;

export const FGOVenueFormValidatorSchema = z.object({
  // Required for the same reason as in {@link VenueFormValidatorSchema}.
  timeZone: z
    .string({ error: 'general_label_required' })
    .min(1, 'general_label_required'),
  /**
   * Google Places search box backing the location picker. It only drives
   * city/country/latitude/longitude and is dropped before the venue is sent.
   */
  addressLine: z.string().optional(),
  shortName: NotEmptyDefaultTranslationValidationSchemaWithLimit,
  country: NotEmptyDefaultTranslationValidationSchema,
  city: NotEmptyDefaultTranslationValidationSchema,
  conditions: NotEmptyDefaultTranslationValidationSchema,
  longitude: z.union([z.string(), z.number()]),
  latitude: z.union([z.string(), z.number()]),
  currency: z.string(),
  name: NotEmptyDefaultTranslationValidationSchemaWithLimit,
  imageFileUrl: z.any().refine((val) => !!val, {
    message: 'Venue image is required',
  }),
});

export type FGOVenueFormValidator = z.infer<typeof FGOVenueFormValidatorSchema>;

export type VenueConfigBody = {
  categories: string[];
  name: string;
  // seatingMap
};
